import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {leaveBreadcrumb, reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {OfflineError} from '../../../../srcCommon/services/offline/offlineError';
import {readApiModel} from '../../../helpers/apiHelpers';
import {inAnimationFrameOrHiddenCallbackAsync} from '../../../helpers/async/inAnimationFrame';
import {inIdleOrHiddenCallbackAsync} from '../../../helpers/async/inIdleCallback';
import {CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {CellsConversationsBoundary} from '../../../helpers/cellsHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {
  ApiConversationPageDataGroup,
  ApiConversationSyncDataGroup,
  ApiMessagePageDataGroup,
  DataGroupModel,
} from '../../../models/api/dataGroupModel';
import {fetchApiAsync, fetchAsync} from '../../../services/http/http';
import {HttpError, HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {AppState} from '../../appState';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompany} from '../../data/company/companySelectors';
import {updateDataAction} from '../../data/dataGroupsActions';
import {getUserId} from '../../data/teammates/teammatesSelectors';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {makeCellsContext} from '../../panels/cells/context/cellsContextHelpers';
import {
  fetchInitialCellsConversationsFromDataGroup,
  fetchMoreCellsConversationsFromDataGroup,
} from '../../panels/cells/conversations/cellsPanelConversationsHelpers';
import {
  buildCellsPathAndQuery,
  buildMoreCellsPathAndQuery,
} from '../../panels/cells/helpers/cellsPanelPathHelpers';
import {fetchInitialCellsMessagesFromDataGroup} from '../../panels/cells/messages/cellsPanelMessagesHelpers';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../panels/inboxes/parameters/inboxesParameters';
import {CellsIdForParameters, CellsListRange, CellsSyncContext} from './cellsList/cellsListState';
import {
  computeCellsListsMinSyncedAt,
  HasFetchedAt,
  hasFetchedAt,
  postProcessApiCellsDataGroupFetchedAt,
} from './cellsListsHelpers';
import {getCellsLists} from './cellsListsSelectors';

/*
 * Type enum.
 */

export enum CellsListsActionsEnum {
  PREFETCH_CELLS_COMPLETED = '@@data/cellsLists/PREFETCH_CELLS_COMPLETED',
  SYNC_COMPLETED = '@@data/cellsLists/SYNC_COMPLETED',
  SYNC_FAILED = '@@data/cellsLists/SYNC_FAILED',
}

/*
 * Types.
 */

interface PrefetchCellsCompletedPayload<T extends InboxesParameters>
  extends CellsListRange<CellsIdForParameters<T>> {
  cellsContext: CellsContext;
  parameters: T;
}

interface PrefetchCellsCompletedAction<T extends InboxesParameters>
  extends ActionWithPayload<PrefetchCellsCompletedPayload<T>> {
  type: CellsListsActionsEnum.PREFETCH_CELLS_COMPLETED;
}

interface SyncPayload {
  context: CellsSyncContext;
}

interface SyncCompletedAction extends ActionWithPayload<SyncCompletedPayload> {
  type: CellsListsActionsEnum.SYNC_COMPLETED;
}

interface SyncCompletedPayload extends SyncPayload {
  prevSyncedAt: number;
  syncedAt: number;
}

interface SyncFailedAction extends ActionWithPayload<SyncFailedPayload> {
  type: CellsListsActionsEnum.SYNC_FAILED;
}

interface SyncFailedPayload extends SyncPayload {}

export type CellsListAction =
  | PrefetchCellsCompletedAction<InboxesParameters>
  | SyncCompletedAction
  | SyncFailedAction;

/*
 * Action creators.
 */

const handlePrefetchResponseIdleTimeout = 1000;

export function prefetchCellsAsyncAction(parameters: InboxesParameters): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const pathAndQuery = buildCellsPathAndQuery(state, parameters);
    if (!pathAndQuery) {
      return;
    }

    // Don't use fetchApiAsync, so we can do the heavy parsing and io-ts decoding in the idle callback.
    const response = await fetchAsync(`${company.url}${pathAndQuery}`, {
      headers: {
        'X-Front-Prefetch':
          parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES
            ? 'messages-prefetcher'
            : 'conversations-prefetcher',
      },
    }).catch((error) => {
      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        throw new OfflineError(error);
      }

      throw error;
    });

    // Try to do as much of the heavy lifting in an idle callback.
    await inIdleOrHiddenCallbackAsync(
      async () => {
        const unprocessedDataGroup = readApiModel(
          parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES
            ? ApiMessagePageDataGroup
            : ApiConversationPageDataGroup,
          response,
        );
        const dataGroup = postProcessApiCellsDataGroupFetchedAt(unprocessedDataGroup);

        const cellsContext = makeCellsContext(state, parameters);

        const result =
          parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES
            ? fetchInitialCellsMessagesFromDataGroup(parameters, dataGroup)
            : fetchInitialCellsConversationsFromDataGroup(cellsContext, parameters, dataGroup);
        if (!hasFetchedAt(result)) {
          return;
        }

        // Don't perform DOM operations in the idle callback.
        await inAnimationFrameOrHiddenCallbackAsync(
          () => {
            batch(() => {
              dispatch(updateDataAction(dataGroup));
              dispatch(prefetchCellsCompletedAction(cellsContext, parameters, result));
            });
          },
          {source: TaskSourcesEnum.PREFETCH},
        );
      },
      {source: TaskSourcesEnum.PREFETCH},
      handlePrefetchResponseIdleTimeout,
    );
  };
}

export function prefetchMoreConversationsAsyncAction(
  parameters: InboxesConversationsParameters,
  referenceBoundary: CellsConversationsBoundary,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const pathAndQuery = buildMoreCellsPathAndQuery(
      state,
      parameters,
      ScrollDirectionsEnum.BOTTOM,
      referenceBoundary,
    );
    if (!pathAndQuery) {
      return;
    }

    // Don't use fetchApiAsync, so we can do the heavy parsing and io-ts decoding in the idle callback.
    const response = await fetchAsync(`${company.url}${pathAndQuery}`, {
      headers: {
        'X-Front-Prefetch': 'conversations-prefetcher',
      },
    }).catch((error) => {
      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        throw new OfflineError();
      }

      throw error;
    });

    // Try to do as much of the heavy lifting in an idle callback.
    await inIdleOrHiddenCallbackAsync(
      async () => {
        const unprocessedDataGroup = readApiModel(ApiConversationPageDataGroup, response);
        const dataGroup = postProcessApiCellsDataGroupFetchedAt(unprocessedDataGroup);

        const cellsContext = makeCellsContext(state, parameters);
        const result = fetchMoreCellsConversationsFromDataGroup(
          cellsContext,
          parameters,
          ScrollDirectionsEnum.BOTTOM,
          referenceBoundary,
          dataGroup,
        );
        if (!hasFetchedAt(result)) {
          return;
        }

        // Don't perform DOM operations in the idle callback.
        await inAnimationFrameOrHiddenCallbackAsync(
          () => {
            batch(() => {
              dispatch(updateDataAction(dataGroup));
              dispatch(prefetchCellsCompletedAction(cellsContext, parameters, result));
            });
          },
          {source: TaskSourcesEnum.PREFETCH},
        );
      },
      {source: TaskSourcesEnum.PREFETCH},
      handlePrefetchResponseIdleTimeout,
    );
  };
}

export function prefetchCellsCompletedAction<T extends InboxesParameters>(
  cellsContext: CellsContext,
  parameters: T,
  result: CellsListRange<CellsIdForParameters<T>>,
): PrefetchCellsCompletedAction<T> {
  return {
    type: CellsListsActionsEnum.PREFETCH_CELLS_COMPLETED,
    payload: {
      ...result,
      cellsContext,
      parameters,
    },
  };
}

export enum SyncCellsListsReasonsEnum {
  BOOT = 'boot',
  PREFETCH = 'prefetch',
  RECONNECT = 'reconnect',
}

export function syncCellsListsAsyncAction(
  context: CellsSyncContext,
  reason: SyncCellsListsReasonsEnum,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  const isPrefetch = reason === SyncCellsListsReasonsEnum.PREFETCH;

  return async (dispatch, getState) => {
    const state = getState();

    // Find the oldest sync time across the same personal workspace.
    const lists = getCellsLists(state);
    const minSyncedAt = computeCellsListsMinSyncedAt(lists, context);
    if (!minSyncedAt) {
      return;
    }

    const loggerContext = {
      sync_age: Date.now() - minSyncedAt,
      sync_reason: reason,
    };

    try {
      // Try to sync.
      const result = await syncCellsListsAsync(state, context, minSyncedAt, cancelToken, isPrefetch);

      // Report sync successes/failures.
      if (result) {
        logger.info('P2 sync succeeded', {
          ...loggerContext,
          size: result.conversations ? result.conversations.length : 0,
        });
      } else {
        logger.warn('P2 sync failed', loggerContext);
      }

      // If the sync failed, we need to re-prime the cache.
      if (!result) {
        dispatch<SyncFailedAction>({
          type: CellsListsActionsEnum.SYNC_FAILED,
          payload: {
            context,
          },
        });
        return;
      }

      await inAnimationFrameOrHiddenCallbackAsync(
        () => {
          batch(() => {
            // Update the data and panels.
            dispatch(updateDataAction(result));

            // Update the lists.
            const {fetchedAt} = result;
            dispatch<SyncCompletedAction>({
              type: CellsListsActionsEnum.SYNC_COMPLETED,
              payload: {
                context,
                prevSyncedAt: minSyncedAt,
                syncedAt: fetchedAt,
              },
            });
          });
        },
        {source: TaskSourcesEnum.PREFETCH},
      );
    } catch (error) {
      if (!(error instanceof CancelError || error instanceof HttpError)) {
        reportToBugsnag(new ExtendedError('Failed to sync cells', error));
      }

      throw error;
    }
  };
}

export function trySyncUserCellsListsAsyncAction(
  reason: SyncCellsListsReasonsEnum,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);

    try {
      await dispatch(syncCellsListsAsyncAction({teammateId: userId}, reason, cancelToken));
    } catch (error) {
      if (error instanceof HttpError || error instanceof CancelError) {
        return;
      }

      reportToBugsnag(new ExtendedError(`Failed to sync P2 after ${reason}`, error));
    }
  };
}

/*
 * Helpers.
 */

async function syncCellsListsAsync(
  state: AppState,
  context: CellsSyncContext,
  prevSyncedAt: number,
  cancelToken?: CancelToken,
  isPrefetch?: boolean,
): Promise<HasFetchedAt<DataGroupModel> | undefined> {
  leaveBreadcrumb(`[syncCellsListsAction] Synchronizing after ${prevSyncedAt}`, {});

  const company = getCompany(state);

  // TODO: Decode in rIC.
  const result = await fetchApiAsync(
    ApiConversationSyncDataGroup,
    `${company.url}/team/${context.teammateId}/sync2?after=${prevSyncedAt}`,
    {
      headers: isPrefetch ? {'X-Front-Prefetch': 'conversations-prefetcher'} : undefined,
      cancelToken,
      shouldParseInIdleCallback: true,
    },
  );

  // If no sync time is present, we need to re-prime the cache.
  if (!hasFetchedAt(result)) {
    return undefined;
  }

  return postProcessApiCellsDataGroupFetchedAt(result);
}
